import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  Eye,
  EyeOff,
  Users as UsersIcon,
  UserPlus,
  DollarSign,
  FileText,
  Download,
  Trash2,
  Plus,
} from "lucide-react";
import PageShell from "../dashboard/PageShell";

const readOnlyCell = (label: string, value?: React.ReactNode) => (
  <tr>
    <td className="px-4 py-2 text-sm text-gray-700 w-64 align-top bg-gray-50 border-b border-gray-200">
      {label}
    </td>
    <td className="px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
      {value ?? <span className="text-gray-400">-</span>}
    </td>
  </tr>
);

const OngoingProjectDetail: React.FC = () => {
  const navigate = useNavigate();
  const id = useMemo(() => {
    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      return parts[parts.length - 1] || "";
    } catch {
      return "";
    }
  }, []);
  const [activeTab, setActiveTab] = useState(() => {
    return (
      localStorage.getItem("ongoingProjectDetailActiveTab") || "topic-info"
    );
  });

  const data = useMemo(
    () => ({
      topicName: "Nghiên cứu phần mềm quản lý KHCN có ứng dụng AI",
      managementLevel: "Cấp Viện/ Chi nhánh",
      topicType: "Đề tài",
      leadUnit: "Trung tâm Đào tạo và Hợp tác Quốc tế",
      chair: "Thượng tá, VS. ThS Lê Anh Tuấn",
      secretary: "Thượng tá, GS. TS Nguyễn Thị Hạnh",
      researchField: "Công nghệ thông tin và tự động hóa",
      duration: "12/2025 - 12/2025",
      provinceCity: "Hà Nội",
      proposedBudget: "10.000.000.000 đ (10 tỷ)",
      objective:
        "Xây dựng phần mềm phục vụ quản trị KHCN tại đơn vị gồm 1000 nghiên cứu viên",
      novelty: "Mới",
      mainContent:
        "Phần mềm triển khai trong toàn đơn vị với dự kiến 25 đề tài/năm",
      application: "Ứng dụng cấp đơn vị",
      expectedEffect: "Ứng dụng cho mạng lưới",
    }),
    []
  );

  const [showDetails, setShowDetails] = useState(false);

  const [showAddAttachment, setShowAddAttachment] = useState(false);
  const [showEditAttachment, setShowEditAttachment] = useState(false);
  const [attachmentForm, setAttachmentForm] = useState({
    name: "",
    file: null as File | null,
  });
  const handleAttachmentChange = (
    field: string,
    value: string | File | null
  ) => {
    setAttachmentForm((prev) => ({ ...prev, [field]: value }));
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState<
    "member" | "progress" | "attachment" | "funding" | "product"
  >("member");
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
    localStorage.setItem("ongoingProjectDetailActiveTab", tabKey);
  };

  const handleDeleteClick = (
    type: "member" | "progress" | "attachment" | "funding" | "product",
    index: number
  ) => {
    setDeleteType(type);
    setDeleteIndex(index);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    console.log(`Delete ${deleteType} at index ${deleteIndex}`);
    setShowDeleteConfirm(false);
    setDeleteType("member");
    setDeleteIndex(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteType("member");
    setDeleteIndex(null);
  };

  return (
    <PageShell title="Chi tiết đề tài">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => window.history.back()}
              aria-label="Go back"
              className="p-3 rounded-full text-[#337ab7] bg-blue-50 hover:bg-blue-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-800 truncate">
              Chi tiết đề tài
              <span className="ml-2 text-sm font-normal text-gray-600 truncate align-middle">
                – {data.topicName}
              </span>
            </h1>
          </div>
          <button
            onClick={() => navigate(`/ongoing-projects/detail/${id}/edit`)}
            className="bg-[#337ab7] text-white px-4 py-2 rounded text-sm font-medium hover:bg-[#2e6da4] transition-colors flex items-center space-x-2 justify-center min-w-[110px]"
          >
            <Pencil className="h-4 w-4" />
            <span>Sửa</span>
          </button>
        </div>

        <div className="bg-white rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6 overflow-x-auto">
              {[
                { key: "topic-info", label: "Thông tin đề tài" },
                { key: "members", label: "Thành viên tham gia" },
                { key: "full-content", label: "Nội dung đầy đủ" },
                { key: "funding", label: "Kinh phí" },
                { key: "progress", label: "Báo cáo tiến độ" },
                { key: "attachments", label: "Tài liệu đính kèm" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => handleTabChange(t.key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === t.key
                      ? "border-[#337ab7] text-[#337ab7]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === "topic-info" && (
              <div className="space-y-6">
                <table className="w-full border border-gray-200 rounded-md overflow-hidden">
                  <tbody>
                    {readOnlyCell("Tên đề tài", data.topicName)}
                    {readOnlyCell("Cấp quản lý", data.managementLevel)}
                    {readOnlyCell("Dạng đề tài", data.topicType)}
                    {readOnlyCell("Đơn vị chủ trì thực hiện", data.leadUnit)}
                    {readOnlyCell("Chủ nhiệm đề tài", data.chair)}
                    {readOnlyCell("Thư ký đề tài", data.secretary)}
                    {readOnlyCell("Lĩnh vực nghiên cứu", data.researchField)}
                    {readOnlyCell("Thời gian thực hiện", data.duration)}
                    {readOnlyCell("Tỉnh / Thành phố", data.provinceCity)}
                    {readOnlyCell("Kinh phí đề xuất", data.proposedBudget)}
                  </tbody>
                </table>

                <div className="mb-2">
                  <button
                    type="button"
                    onClick={() => setShowDetails((v) => !v)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50"
                  >
                    {showDetails ? (
                      <EyeOff className="h-4 w-4 text-[#337ab7]" />
                    ) : (
                      <Eye className="h-4 w-4 text-[#337ab7]" />
                    )}
                    <span>Xem chi tiết thông tin</span>
                  </button>
                </div>
                {showDetails && (
                  <table className="w-full border border-gray-200 rounded-md overflow-hidden">
                    <tbody>
                      {readOnlyCell("Mục tiêu", data.objective)}
                      {readOnlyCell(
                        "Tính cấp thiết và tính mới của nhiệm vụ",
                        data.novelty
                      )}
                      {readOnlyCell("Nội dung chính", data.mainContent)}
                      {readOnlyCell("Kết quả dự kiến", data.expectedEffect)}
                      {readOnlyCell(
                        "Khả năng và địa chỉ ứng dụng",
                        data.application
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* products tab intentionally not present for ongoing projects */}
            {false && activeTab === "products" && (
              <div className="space-y-8">
                {/* SẢN PHẨM DẠNG I */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">
                    SẢN PHẨM DẠNG I
                  </h3>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 text-[#337ab7] text-sm hover:underline"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Thêm sản phẩm dạng I</span>
                  </button>
                  <div className="border rounded">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-gray-700">
                          <th className="px-4 py-2 w-10">#</th>
                          <th className="px-4 py-2 text-left">Tên sản phẩm</th>
                          <th className="px-4 py-2 text-left">Đơn vị tính</th>
                          <th className="px-4 py-2 text-left">Thông số</th>
                          <th className="px-4 py-2 text-left">Số lượng</th>
                          <th className="px-4 py-2 w-28 text-center">
                            Chức năng
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          {
                            id: 1,
                            name: "1111",
                            unit: "111",
                            spec: "»",
                            qty: "",
                          },
                          { id: 2, name: "", unit: "", spec: "»", qty: "" },
                        ].map((row, idx) => (
                          <tr key={row.id} className="border-t">
                            <td className="px-4 py-2 text-center">{idx + 1}</td>
                            <td className="px-4 py-2">{row.name}</td>
                            <td className="px-4 py-2">{row.unit}</td>
                            <td className="px-4 py-2">{row.spec}</td>
                            <td className="px-4 py-2">{row.qty}</td>
                            <td className="px-4 py-2 text-center">
                              <div className="inline-flex items-center gap-3">
                                <button
                                  className="text-blue-600 hover:text-blue-800"
                                  aria-label="edit product I"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteClick("product", idx)
                                  }
                                  className="text-red-600 hover:text-red-800"
                                  aria-label="delete product I"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* SẢN PHẨM DẠNG II */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">
                    SẢN PHẨM DẠNG II
                  </h3>
                </div>

                {/* SẢN PHẨM DẠNG III */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">
                    SẢN PHẨM DẠNG III
                  </h3>
                  <div className="flex gap-6 flex-wrap">
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 text-[#337ab7] text-sm hover:underline"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Thêm tạp chí trong nước, quốc tế</span>
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 text-[#337ab7] text-sm hover:underline"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Thêm sách chuyên khảo</span>
                    </button>
                  </div>
                  <div className="border rounded">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-gray-700">
                          <th className="px-4 py-2 w-10">#</th>
                          <th className="px-4 py-2 text-left">
                            Tên công trình
                          </th>
                          <th className="px-4 py-2 text-left">Tác giả</th>
                          <th className="px-4 py-2 text-left">Đồng tác giả</th>
                          <th className="px-4 py-2 text-left">
                            Nơi công bố (Tên tạp chí đã đăng/ Nhà xuất bản)
                          </th>
                          <th className="px-4 py-2 text-left">Năm công bố</th>
                          <th className="px-4 py-2 w-28 text-center">
                            Chức năng
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { id: "I", title: "Tạp chí quốc tế" },
                          { id: "II", title: "Tạp chí trong nước" },
                          { id: "III", title: "Sách chuyên khảo" },
                        ].map((row, idx) => (
                          <tr key={row.id} className="border-t">
                            <td className="px-4 py-2">{row.id}</td>
                            <td className="px-4 py-2">{row.title}</td>
                            <td className="px-4 py-2"></td>
                            <td className="px-4 py-2"></td>
                            <td className="px-4 py-2"></td>
                            <td className="px-4 py-2"></td>
                            <td className="px-4 py-2 text-center">
                              <div className="inline-flex items-center gap-3">
                                <button
                                  className="text-blue-600 hover:text-blue-800"
                                  aria-label="edit product III"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteClick("product", idx)
                                  }
                                  className="text-red-600 hover:text-red-800"
                                  aria-label="delete product III"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "members" && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <button
                    onClick={() =>
                      navigate(`/ongoing-projects/detail/${id}/members/add`)
                    }
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm"
                  >
                    <UserPlus className="h-4 w-4" />
                    <span>Thêm thành viên tham gia</span>
                  </button>
                </div>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                            #
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Họ tên
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Chức danh thực hiện
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Điện thoại
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Email
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Đơn vị
                          </th>
                          <th className="px-3 py-2 text-center text-xs font-bold text-black uppercase tracking-wider w-32">
                            Chức năng
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">
                            1
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            Nguyễn Văn A
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200"></td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            0900000000
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            a@example.com
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            Viện ABC
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex justify-center space-x-4">
                              <button
                                className="text-[#337ab7] hover:underline text-lg"
                                title="Sửa"
                              >
                                ✎
                              </button>
                              <button
                                className="text-red-600 hover:underline text-lg"
                                title="Xóa"
                              >
                                🗑
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "full-content" && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="text-sm text-gray-700 leading-relaxed space-y-3">
                  <p>
                    Xây dựng phần mềm quản lý KHCN phục vụ 1000 nghiên cứu viên.
                  </p>
                  <p>
                    Nội dung chính gồm: Phân tích và thiết kế hệ thống, phát
                    triển module, tích hợp AI, triển khai.
                  </p>
                </div>
              </div>
            )}

            {activeTab === "funding" && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <button
                    onClick={() =>
                      navigate(`/ongoing-projects/detail/${id}/funding/add`)
                    }
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm"
                  >
                    <DollarSign className="h-4 w-4" />
                    <span>Thêm kinh phí phê duyệt - đã cấp</span>
                  </button>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                            #
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Nguồn kinh phí
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Năm
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Kinh phí phê duyệt
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Kinh phí đã cấp
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Ngày cấp
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Ghi chú
                          </th>
                          <th className="px-3 py-2 text-center text-xs font-bold text-black uppercase tracking-wider w-32">
                            Chức năng
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">
                            1
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            Ngân sách giao tự chủ cho viện
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            2022
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            100.000.000 đ
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            50.000.000 đ
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            15/11/2022
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            —
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex justify-center space-x-4">
                              <button
                                className="text-[#337ab7] hover:underline text-lg"
                                title="Sửa"
                              >
                                ✎
                              </button>
                              <button
                                className="text-red-600 hover:underline text-lg"
                                title="Xóa"
                              >
                                🗑
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "progress" && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <button
                    onClick={() =>
                      navigate(`/ongoing-projects/detail/${id}/progress/add`)
                    }
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Thêm báo cáo tiến độ</span>
                  </button>
                </div>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            STT
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Tháng
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Nội dung báo cáo
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Kế hoạch tháng tiếp theo
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            File đính kèm
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Ngày tháng
                          </th>
                          <th className="px-3 py-2 text-center text-xs font-bold text-black uppercase tracking-wider w-32">
                            Chức năng
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">
                            1
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            05/2022
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            Báo cáo nội dung
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            Kế hoạch tháng sau
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            —
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                            10/11/2022 23:20
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex justify-center space-x-4">
                              <button
                                className="text-[#337ab7] hover:underline text-lg"
                                title="Sửa"
                              >
                                ✎
                              </button>
                              <button
                                className="text-red-600 hover:underline text-lg"
                                title="Xóa"
                              >
                                🗑
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "attachments" && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowAddAttachment(true)}
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm"
                  >
                    <Plus className="h-4 w-4" />
                    Thêm tài liệu đính kèm của đề tài
                  </button>
                </div>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            STT
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                            Tên file
                          </th>
                          <th className="px-3 py-2 text-center text-xs font-bold text-black uppercase tracking-wider w-32">
                            Chức năng
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {[
                          "Quyết định Phê duyệt danh mục",
                          "Thuyết minh nhiệm vụ",
                        ].map((fileName, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">
                              {index + 1}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                              {fileName}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex justify-center space-x-2">
                                <button
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-1 rounded transition-colors"
                                  title="Tải xuống"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => setShowEditAttachment(true)}
                                  className="text-[#337ab7] hover:text-blue-700 hover:bg-blue-50 p-1 rounded transition-colors"
                                  title="Sửa"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setDeleteType("attachment");
                                    setShowDeleteConfirm(true);
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                                  title="Xóa"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add Attachment Modal */}
        {showAddAttachment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Thêm tài liệu đính kèm
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tên tài liệu <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    type="text"
                    value={attachmentForm.name}
                    onChange={(e) =>
                      handleAttachmentChange("name", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập tên tài liệu..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File đính kèm <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    type="file"
                    onChange={(e) =>
                      handleAttachmentChange(
                        "file",
                        e.target.files?.[0] || null
                      )
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowAddAttachment(false);
                    setAttachmentForm({ name: "", file: null });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  onClick={() => {
                    setShowAddAttachment(false);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium"
                >
                  Lưu
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Attachment Modal */}
        {showEditAttachment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Sửa tài liệu đính kèm
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tên tài liệu <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    type="text"
                    value={attachmentForm.name}
                    onChange={(e) =>
                      handleAttachmentChange("name", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập tên tài liệu..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File đính kèm
                  </label>
                  <input
                    type="file"
                    onChange={(e) =>
                      handleAttachmentChange(
                        "file",
                        e.target.files?.[0] || null
                      )
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowEditAttachment(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  onClick={() => {
                    setShowEditAttachment(false);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium"
                >
                  Lưu
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
            <div className="bg-white rounded-md p-4 max-w-sm w-full mx-4">
              <h3 className="text-base font-semibold text-gray-900 mb-2">
                Xác nhận xóa
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Bạn có chắc muốn xóa?
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={handleDeleteCancel}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default OngoingProjectDetail;
